/**
 * Kids Search Module
 * Provides live search functionality for the Kids block
 */

import { onDOMReady, devLog } from './utils.js';

class KidsSearch {
    constructor() {
        this.searchInputs = document.querySelectorAll('.kids-block__search .form-input, .kids-search-input');
        this.kidsBlocks = document.querySelectorAll('.kids-block--archive');
        this.debounceDelay = 300;
        this.searchTimeouts = new Map();
        this.originalGridHTML = new Map();
        
        this.init();
    }

    init() {
        if (this.searchInputs.length === 0) return;
        
        this.searchInputs.forEach((input, index) => {
            this.setupSearchInput(input, index);
        });
    }

    setupSearchInput(input, index) {
        const block = input.closest('.kids-block');
        const grid = block?.querySelector('.kids-grid');
        const noResults = block?.querySelector('.kids-block__no-results');
        const searchContainer = input.closest('.kids-block__search');
        
        if (!grid) return;

        // Cache the initial grid HTML so clearing search restores the original list.
        // Without this, a cleared search would keep showing the previous search results.
        if (!this.originalGridHTML.has(index)) {
            this.originalGridHTML.set(index, grid.innerHTML);
        }

        // Add event listeners
        input.addEventListener('input', (e) => {
            this.handleSearch(e.target.value, grid, noResults, index);
        });

        // Toggle parent state class so CSS can react (e.g. hide search icon ::after when clear button shows)
        const updateParentState = () => {
            if (!searchContainer) return;
            if (input.value.trim()) {
                searchContainer.classList.add('kids-search-has-value');
            } else {
                searchContainer.classList.remove('kids-search-has-value');
            }
        };
        input.addEventListener('input', updateParentState);
        // Ensure initial state is correct (e.g. back/forward cache)
        updateParentState();

        input.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                input.value = '';
                this.handleSearch('', grid, noResults, index);
                updateParentState();
                input.blur();
            }
        });

        // Add clear button functionality
        this.addClearButton(input, grid, noResults, index);
    }

    handleSearch(searchTerm, grid, noResults, index) {
        // Clear existing timeout
        if (this.searchTimeouts.has(index)) {
            clearTimeout(this.searchTimeouts.get(index));
        }

        // Set new timeout for debouncing
        const timeout = setTimeout(() => {
            this.performSearch(searchTerm, grid, noResults, index);
        }, this.debounceDelay);

        this.searchTimeouts.set(index, timeout);
    }

    performSearch(searchTerm, grid, noResults, index) {
        // If search is empty, show all cards and re-enable infinite scroll
        if (searchTerm === '') {
            // Restore the initial grid contents (before any search ran)
            const initialHTML = this.originalGridHTML.get(index);
            if (initialHTML) {
                grid.innerHTML = initialHTML;
            }

            const cards = grid.querySelectorAll('.prop-card');
            cards.forEach(card => {
                card.style.display = '';
                card.style.visibility = 'visible';
                card.style.opacity = '1';
                card.style.transform = 'scale(1)';
            });
            
            if (noResults) {
                noResults.style.display = 'none';
            }
            
            // Trigger custom event for infinite scroll to re-enable
            grid.dispatchEvent(new CustomEvent('kidsSearchUpdate', {
                detail: { visibleCount: cards.length, searchTerm: '' }
            }));
            return;
        }
        
        // For non-empty search, make server-side request
        this.performServerSearch(searchTerm, grid, noResults, index);
    }
    
    getOrCreateNoResultsElement(block, grid) {
        // Prefer server-rendered element if present
        let el = block?.querySelector('.kids-block__no-results');
        if (el) return el;

        // Create a fallback message container above the grid
        el = document.createElement('div');
        el.className = 'kids-block__no-results';
        el.style.display = 'none';
        el.style.opacity = '0';
        el.innerHTML = '<p></p>';

        grid.parentNode?.insertBefore(el, grid);
        return el;
    }

    async performServerSearch(searchTerm, grid, noResults, index) {
        const block = grid.closest('.kids-block');
        const blockId = block.dataset.blockId;
        const maxItems = parseInt(block.dataset.maxItems) || 6;
        const taxonomyFilter = block.dataset.taxonomyFilter || '';
        const randomize = block.dataset.randomize === 'true';
        const nonce = block.dataset.nonce || '';

        const noResultsEl = noResults || this.getOrCreateNoResultsElement(block, grid);
        
        // Show loading state
        const cards = grid.querySelectorAll('.prop-card');
        cards.forEach(card => {
            card.style.opacity = '0.5';
        });
        
        try {
            const formData = new FormData();
            formData.append('action', 'search_kids');
            formData.append('search_term', searchTerm);
            formData.append('max_items', maxItems);
            formData.append('taxonomy_filter', taxonomyFilter);
            formData.append('randomize', randomize);
            formData.append('block_id', blockId);
            formData.append('nonce', nonce);
            
            const response = await fetch(ajaxurl, {
                method: 'POST',
                body: formData
            });
            
            const result = await response.json();
            
            if (result.success && result.data && typeof result.data.html === 'string') {
                // Replace all cards with search results (may be empty)
                grid.innerHTML = result.data.html;
                
                // Animate in the new cards
                const newCards = grid.querySelectorAll('.prop-card');

                // If no matches, restore the unfiltered list and show an explicit message above the grid.
                if (newCards.length === 0) {
                    const initialHTML = this.originalGridHTML.get(index);

                    if (initialHTML) {
                        grid.innerHTML = initialHTML;
                    }

                    if (noResultsEl) {
                        const p = noResultsEl.querySelector('p') || noResultsEl;
                        p.textContent = `No kids found matching “${searchTerm}”. Showing all kids.`;
                        noResultsEl.style.display = 'block';
                        noResultsEl.style.visibility = 'visible';
                        noResultsEl.style.opacity = '0';
                        noResultsEl.style.transform = 'scale(0.95)';
                        requestAnimationFrame(() => {
                            noResultsEl.style.opacity = '1';
                            noResultsEl.style.transform = 'scale(1)';
                        });
                    }

                    const restoredCards = grid.querySelectorAll('.prop-card');
                    grid.dispatchEvent(new CustomEvent('kidsSearchUpdate', {
                        detail: { visibleCount: restoredCards.length, searchTerm: '' }
                    }));
                    return;
                }

                newCards.forEach((card, index) => {
                    card.style.opacity = '0';
                    card.style.transform = 'scale(0.95)';
                    
                    setTimeout(() => {
                        requestAnimationFrame(() => {
                            card.style.transition = 'opacity 0.4s ease-out, transform 0.4s ease-out';
                            card.style.opacity = '1';
                            card.style.transform = 'scale(1)';
                        });
                    }, index * 50);
                });
                
                // Show/hide no results message
                if (noResultsEl) {
                    noResultsEl.style.display = 'none';
                }
                
                // Trigger custom event for infinite scroll to disable
                grid.dispatchEvent(new CustomEvent('kidsSearchUpdate', {
                    detail: { visibleCount: newCards.length, searchTerm }
                }));
            } else {
                devLog('Search failed:', result);
                // Restore original cards
                cards.forEach(card => {
                    card.style.opacity = '1';
                });
            }
        } catch (error) {
            devLog('Search error:', error);
            // Restore original cards
            cards.forEach(card => {
                card.style.opacity = '1';
            });
        }
    }

    addClearButton(input, grid, noResults, index) {
        const formGroup = input.closest('.form-group');
        const searchContainer = input.closest('.kids-block__search');
        if (!formGroup) return;

        const clearButton = document.createElement('button');
        clearButton.type = 'button';
        clearButton.className = 'kids-block__search-clear';
        clearButton.innerHTML = '×';
        clearButton.setAttribute('aria-label', 'Clear search');
        clearButton.style.display = 'none';

        clearButton.addEventListener('click', () => {
            input.value = '';
            this.handleSearch('', grid, noResults, index);
            if (searchContainer) {
                searchContainer.classList.remove('kids-search-has-value');
            }
            input.focus();
        });

        // Show/hide clear button based on input value
        input.addEventListener('input', () => {
            if (input.value.trim()) {
                clearButton.style.display = 'flex';
            } else {
                clearButton.style.display = 'none';
            }
        });

        formGroup.appendChild(clearButton);
    }
}

// Initialize when DOM is ready
onDOMReady(() => {
    new KidsSearch();
});

export default KidsSearch; 